import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import authLocation from 'assets/android-helpers/auth_location.png';
import authSuperpositionGif from 'assets/android-helpers/auth_superposition.gif';
import authXiaomiGif from 'assets/android-helpers/auth_xiaomi.gif';
import authUsageGif from 'assets/android-helpers/auth_usage.gif';
import AltusTalk from 'components/altus-talk/altus-talk';
import AndroidApplicationList from 'components/android-application-list/android-application-list';
import AndroidRequestPermission from 'components/android-request-permission/android-request-permission';
import AndroidRequestSuccess from 'components/android-request-permission/android-request-success';
import BackHeader from 'components/back-header/back-header';
import Button from 'components/button/button';
import Flex from 'components/flex/flex';
import LoadingScreen from 'components/loading-screen/loading-screen';
import Page from 'components/page/page';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SegmentEventType, track } from 'segment';
import BlockerPlugin from 'utils/BlockerPlugin';

/* eslint-disable-next-line */
export interface BlockerProps {}

export interface Application {
  name: string;
  package: string;
  icon: string;
  category?: number;
}

export function getApplicationCategoryTitle(category?: number): string {
  switch (category) {
    case 0:
      return 'Jeux vidéo';
    case 1:
      return 'Musique et audio';
    case 2:
      return 'Lecteurs vidéo';
    case 3:
      return 'Images';
    case 4:
      return 'Réseaux sociaux / Navigateurs';
    case 5:
      return 'Actualités';
    case 6:
      return 'Cartes et navigation';
    case 7:
      return 'Productivité';
    case 8:
      return 'Accessibilité';
    default:
      return 'Autres';
  }
}

export function BlockerAndroid(props: BlockerProps) {
  const [applicationList, setApplicationList] = useState<Application[]>();
  const [blockedApplications, setBlockedApplications] = useState<Application[]>();
  const [usageStatsPermission, setUsageStatsPermission] = useState<boolean>();
  const [usageStatsSuccessScreen, setUsageStatsSuccessScreen] = useState<boolean>();
  const [accessibilityPermission, setAccessibilityPermission] = useState<boolean>();
  const [accessibilitySuccessScreen, setAccessibilitySuccessScreen] = useState<boolean>();
  const [deviceAdminPermission, setDeviceAdminPermission] = useState<boolean>();
  const [deviceAdminSuccessScreen, setDeviceAdminSuccessScreen] = useState<boolean>();
  const [manufacturerPermission, setManufacturerPermission] = useState<boolean>();
  const [manufacturer, setManufacturer] = useState<string>();
  const [manufacturerSuccessScreen, setManufacturerSuccessScreen] = useState<boolean>();
  // const [autoStartPermission, setAutoStartPermission] =
  //   useState<boolean>();
  const [drawOverlayPermission, setDrawOverlayPermission] = useState<boolean>();
  const [drawOverlaySuccessScreen, setDrawOverlaySuccessScreen] = useState<boolean>();
  const [gpsPermission, setGpsPermission] = useState<boolean>();
  const { id } = useParams();
  const navigate = useNavigate();

  const [askedForGps, setAskedForGps] = useState(false);

  useEffect(() => {
    const checkPermissions = () => {
      BlockerPlugin.checkUsageStatsPermission().then((res) => {
        setUsageStatsPermission(res.isGranted);
        if (usageStatsSuccessScreen === undefined) {
          setUsageStatsSuccessScreen(res.isGranted);
        }
      });
      BlockerPlugin.checkDrawOverlayPermission().then((res) => {
        setDrawOverlayPermission(res.isGranted);
        if (drawOverlaySuccessScreen === undefined) {
          setDrawOverlaySuccessScreen(res.isGranted);
        }
      });
      BlockerPlugin.checkAccessibilityPermission().then((res) => {
        setAccessibilityPermission(res.isGranted);
        if (accessibilitySuccessScreen === undefined) {
          setAccessibilitySuccessScreen(res.isGranted);
        }
      });
      BlockerPlugin.isAdminActive().then((res) => {
        setDeviceAdminPermission(res.isActive);
        if (deviceAdminSuccessScreen === undefined) {
          setDeviceAdminSuccessScreen(res.isActive);
        }
      });
      BlockerPlugin.checkManufacturerSpecificPermission().then((res) => {
        setManufacturerPermission(res.isGranted);
        setManufacturer(res.manufacturer);
        console.log(res);
        if (manufacturerSuccessScreen === undefined) {
          setManufacturerSuccessScreen(res.isGranted);
        }
      });
      if (askedForGps) {
        setGpsPermission(true);
      } else {
        BlockerPlugin.checkLocationPermission().then((res) => {
          setGpsPermission(res.isGranted);
        });
      }
    };

    track(SegmentEventType.BLOCKER_CONFIGURATION_STARTED, {
      children_id: id,
    });
    BlockerPlugin.getApplicationList().then((res) => {
      setApplicationList(res.applications);
      Preferences.get({ key: 'blocked_applications' })
        .then((blocked) => {
          if (blocked.value) {
            const apps = JSON.parse(blocked.value);
            setBlockedApplications(res.applications.filter((app) => apps.includes(app.package)));
          } else {
            setBlockedApplications([]);
          }
        })
        .catch(() => {
          setBlockedApplications([]);
        });
    });
    checkPermissions();
    if (Capacitor.getPlatform() !== 'android') {
      // setApplicationList(SIMULATED_APPLICATIONS);
      // setDrawOverlayPermission(true);
      // setUsageStatsPermission(true);
      // setBlockedApplications([]);
    }

    const listener = App.addListener('appStateChange', (event) => {
      checkPermissions();
    });

    return () => {
      listener.remove();
    };
  }, [
    askedForGps,
    drawOverlaySuccessScreen,
    id,
    usageStatsSuccessScreen,
    accessibilitySuccessScreen,
    deviceAdminSuccessScreen,
    manufacturerSuccessScreen,
  ]);

  async function setPhoneAsConfigured() {
    await Preferences.set({
      key: 'blockerConfigured',
      value: 'true',
    });
    track(SegmentEventType.BLOCKER_CONFIGURATION_COMPLETED, {
      children_id: id,
    });
  }
  const [search, setSearch] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<number>();

  if (!applicationList) {
    return (
      <LoadingScreen altus="animated_hi" text="Nous récupérons la liste de tes applications... cela peut être long !" />
    );
  }
  console.log(usageStatsSuccessScreen);

  if (!usageStatsPermission) {
    return (
      <AndroidRequestPermission
        childrenId={id ?? ''}
        text="J'ai besoin de la permission pour pouvoir bloquer les applications"
        requestPermission={() => {
          BlockerPlugin.requestUsageStatsPermission();
        }}
        imageUrl={authUsageGif}
        stepNumber={1}
      />
    );
  }
  if (!usageStatsSuccessScreen) {
    return (
      <AndroidRequestSuccess
        childrenId={id ?? ''}
        text="Bravo ! J'ai bien la permission pour bloquer les applications."
        onNext={() => {
          setUsageStatsSuccessScreen(true);
        }}
        stepNumber={1}
      />
    );
  }
  if (!drawOverlayPermission) {
    return (
      <AndroidRequestPermission
        childrenId={id ?? ''}
        text="J'ai besoin de la permission pour pouvoir afficher le bloqueur"
        requestPermission={() => {
          BlockerPlugin.requestDrawOverlayPermission();
        }}
        imageUrl={authSuperpositionGif}
        stepNumber={2}
      />
    );
  }
  if (!drawOverlaySuccessScreen) {
    return (
      <AndroidRequestSuccess
        childrenId={id ?? ''}
        text="Bravo ! J'ai bien la permission pour afficher le bloqueur ! Vous y êtes presque !"
        onNext={() => {
          setDrawOverlaySuccessScreen(true);
        }}
        stepNumber={2}
      />
    );
  }
  if (!deviceAdminPermission) {
    return (
      <AndroidRequestPermission
        childrenId={id ?? ''}
        text="J'ai besoin de la permission pour accéder à l'administration de votre appareil."
        requestPermission={() => {
          BlockerPlugin.requestAdminPrivileges();
        }}
        imageUrl={authUsageGif}
        stepNumber={3}
      />
    );
  }
  if (!deviceAdminSuccessScreen) {
    return (
      <AndroidRequestSuccess
        childrenId={id ?? ''}
        text="Bravo ! J'ai bien la permission pour accéder à l'administration de votre appareil."
        onNext={() => {
          setDeviceAdminSuccessScreen(true);
        }}
        stepNumber={3}
      />
    );
  }
  if (!accessibilityPermission) {
    return (
      <AndroidRequestPermission
        childrenId={id ?? ''}
        text={
          <div style={{ fontSize: 15 }}>
            <b>Pourquoi Altus demande l’accès à l’accessibilité ?</b>
            <br />
            <br />
            Altus utilise l’accès à l’accessibilité exclusivement pour empêcher la désinstallation non autorisée de
            l’application par un enfant. Cette autorisation est nécessaire pour le bon fonctionnement du contrôle
            parental.
            <br />
            <br />
            Aucune donnée n’est collectée, stockée ou partagée via ce service.
            <br />
            <br />
            En cliquant sur “Autoriser Altus”, vous allez être redirigé vers les paramètres système de votre appareil,
            où vous devrez activer manuellement cette autorisation.
          </div>
        }
        requestPermission={() => {
          BlockerPlugin.requestAccessibilityPermission();
        }}
        refusePermission={() => {
          navigate(`/dashboard`);
        }}
        // imageUrl={authUsageGif}
        stepNumber={4}
      />
    );
  }
  if (!accessibilitySuccessScreen) {
    return (
      <AndroidRequestSuccess
        childrenId={id ?? ''}
        text="Bravo ! J'ai bien la permission pour accéder à l'accessibilité de votre appareil."
        onNext={() => {
          setAccessibilitySuccessScreen(true);
        }}
        stepNumber={4}
      />
    );
  }
  if (!manufacturerPermission && manufacturer === 'xiaomi') {
    return (
      <AndroidRequestPermission
        childrenId={id ?? ''}
        text="Sur les appareils Xiaomi, j'ai besoin de la permission pour pouvoir bloquer les applications."
        requestPermission={() => {
          BlockerPlugin.requestManufacturerSpecificPermission();
        }}
        imageUrl={authXiaomiGif}
        stepNumber={4}
      />
    );
  }
  if (!manufacturerSuccessScreen && manufacturer === 'xiaomi') {
    return (
      <AndroidRequestSuccess
        childrenId={id ?? ''}
        text="Bravo ! J'ai bien la permission pour bloquer les applications sur les appareils Xiaomi."
        onNext={() => {
          setManufacturerSuccessScreen(true);
        }}
        stepNumber={4}
      />
    );
  }
  if (!gpsPermission) {
    return (
      <AndroidRequestPermission
        childrenId={id ?? ''}
        text="J'ai besoin d'accéder aux données GPS en tâche de fond pour vous permettre de suivre la géolocalisation de votre enfant depuis l'espace parent. Je l'utilise dans l'unique but de vous permettre de suivre la géolocalisation de votre enfant. Les données ne sont ni stockées ni partagées."
        requestPermission={() => {
          BlockerPlugin.askForLocationPermission();
          setAskedForGps(true);
        }}
        imageUrl={authLocation}
        stepNumber={5}
      />
    );
  }
  return (
    <Page>
      <Flex
        direction="column"
        justify="space-between"
        align="center"
        width="100%"
        height="100%"
        style={{ paddingTop: 65, maxWidth: '100%' }}
      >
        <BackHeader
          title="Paramétrage du bloqueur"
          onClick={() => {
            if (search.length || selectedCategory !== undefined) {
              setSearch('');
              setSelectedCategory(undefined);
              return;
            }
            navigate(`/blocker/${id}`);
          }}
        />
        <Flex
          direction="column"
          style={{
            paddingTop: 20,
            overflowY: 'auto',
          }}
        >
          <Flex pad="10px 20px">
            <AltusTalk variant="small">
              Choisis les applications que tu veux bloquer sur cet appareil, puis cliques sur suivant !
            </AltusTalk>
          </Flex>
          {applicationList && blockedApplications && (
            <AndroidApplicationList
              applications={applicationList}
              setBlockedApplications={setBlockedApplications}
              blockedApplications={blockedApplications}
              search={search}
              setSearch={setSearch}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
            />
          )}
        </Flex>
        <Flex
          direction="column"
          gap={10}
          pad={20}
          width="100%"
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
          }}
        >
          <Button
            disabled={!blockedApplications}
            color="primary"
            onClick={() => {
              Preferences.set({
                key: 'blocked_applications',
                value: JSON.stringify(blockedApplications?.map((app) => app.package) ?? []),
              }).then(() => {
                setPhoneAsConfigured().then(() => {
                  navigate(`/blocker/${id}`);
                });
              });
            }}
          >
            LIMITER
          </Button>
        </Flex>
      </Flex>
    </Page>
  );
}

export default BlockerAndroid;
